// =============================================================================
// features/meu-whatsapp/ConnectWhatsAppPrompt.tsx — Convite "conecte o seu
// WhatsApp" (F56-S03), montado no AppLayout.
//
// Sequência curta: (1) aviso de monitoramento + convite; (2) QR do celular;
// (3) sucesso, que fecha sozinho. "Depois" e Esc dispensam o convite só nesta
// aba do navegador (connectPrompt.ts) e nunca bloqueiam o trabalho.
//
// Política: o QR fica DENTRO do PolicyAcceptanceGate existente (sem alterá-lo,
// sem duplicar texto nem aceite) — o painel do QR só monta depois do aceite.
// O Gate abre o diálogo de ciência como `fixed` dentro do cartão, que por isso
// NÃO usa `transform` (um ancestral transformado prenderia o `fixed` do Gate).
//
// LGPD: o QR vive só no PairingQrPanel e some quando este modal fecha.
// =============================================================================

import { WHATSAPP_MONITORING_POLICY_KEY } from '@elemento/shared-schemas';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../../lib/cn';
import { MonitoringBanner } from '../policies/MonitoringBanner';
import { PolicyAcceptanceGate } from '../policies/PolicyAcceptanceGate';
import { useFocusTrap } from '../sales/components/useFocusTrap';

import { PairingQrPanel } from './PairingQrPanel';
import { useConnectPrompt, useConnectPromptEligibility } from './useConnectPrompt';
import { MEU_WHATSAPP_KEY, useMeuPareamento } from './useMeuWhatsApp';
import { useSessionStatusRealtime } from './useSessionStatusRealtime';

type Step = 'intro' | 'qr' | 'success';

const SUCCESS_AUTO_CLOSE_MS = 2500;

const BTN_BASE =
  'flex-1 min-h-[44px] font-sans text-sm px-4 rounded-sm focus-visible:outline-none focus-visible:ring-2';
const BTN_SECONDARY = cn(
  BTN_BASE,
  'font-medium border border-border-strong text-ink-2 hover:bg-surface-hover',
  'transition-colors duration-fast focus-visible:ring-azul/20',
);
const BTN_PRIMARY = cn(
  BTN_BASE,
  'font-semibold bg-azul text-white hover:brightness-110',
  'transition-[filter] duration-fast focus-visible:ring-azul/30',
);

function Shell({
  titleId,
  children,
}: {
  readonly titleId: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const ref = React.useRef<HTMLDivElement>(null);
  useFocusTrap({ containerRef: ref, active: true });
  // O trap foca o primeiro focável; o convite prefere a ação principal.
  React.useEffect(() => {
    ref.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus();
  }, []);

  return (
    <div className="fixed inset-0 z-[160] flex items-center justify-center p-4">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-[440px] max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-md flex flex-col gap-4 p-6"
        style={{
          background: 'var(--bg-elev-1)',
          border: '1px solid var(--border)',
          boxShadow: 'var(--elev-5)',
          animation: 'fade-in 200ms ease both',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Title({ id, children }: { id: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <h2
      id={id}
      className="font-display font-bold text-ink"
      style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
    >
      {children}
    </h2>
  );
}

export interface ConnectDialogProps {
  readonly onDismiss: () => void;
  readonly onFinish: () => void;
  /**
   * F57-S02: introdução própria (a guarda de conexão mostra motivo e passo a
   * passo). Sem ela, a introdução do convite do F56-S03.
   */
  readonly intro?: { readonly title: string; readonly body: React.ReactNode };
  /**
   * F57-S02: o usuário pode parear sozinho (`channel.self_pair` + flag de
   * pareamento). `false` = sem botão de QR, só "Entendi" — a introdução diz
   * a quem pedir ajuda. Padrão `true` (o convite já é gated por isso).
   */
  readonly canPair?: boolean;
}

/**
 * Diálogo de conexão: introdução → QR (atrás do aceite da política) → sucesso.
 * Reusado pelo convite (F56-S03) e pela guarda de conexão (F57-S02).
 */
export function ConnectDialog({
  onDismiss,
  onFinish,
  intro,
  canPair = true,
}: ConnectDialogProps): React.JSX.Element {
  const [step, setStep] = React.useState<Step>('intro');
  const queryClient = useQueryClient();
  const me = useMeuPareamento();
  const status = me.data?.status;

  // Só existe enquanto o convite está aberto; a página /meu-whatsapp (que monta
  // o mesmo hook) é rota excluída, então nunca coexistem.
  useSessionStatusRealtime();

  React.useEffect(() => {
    if (status !== 'WORKING') return;
    if (step === 'qr') setStep('success');
    else if (step === 'intro') onFinish(); // conectou por outro caminho
  }, [status, step, onFinish]);

  React.useEffect(() => {
    if (step !== 'success') return undefined;
    void queryClient.invalidateQueries({ queryKey: MEU_WHATSAPP_KEY });
    const timer = window.setTimeout(onFinish, SUCCESS_AUTO_CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [step, queryClient, onFinish]);

  // Esc = "Depois" (ou fechar, no sucesso). Listener próprio: com o diálogo de
  // ciência do Gate aberto, o cartão está por baixo e o Esc ainda precisa valer.
  const close = step === 'success' ? onFinish : onDismiss;
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  return createPortal(
    <>
      <div
        aria-hidden="true"
        className="fixed inset-0 z-[150] bg-[var(--text)]/30 backdrop-blur-[2px]"
        style={{ animation: 'fade-in 200ms ease both' }}
      />
      {step === 'intro' ? (
        <Shell titleId="connect-wa-title">
          <Title id="connect-wa-title">{intro?.title ?? 'Conecte seu WhatsApp ao sistema'}</Title>
          {intro !== undefined ? (
            intro.body
          ) : (
            <p className="font-sans text-sm text-ink-2">
              Leva menos de um minuto: você escaneia um código com o celular e suas conversas passam
              a aparecer aqui.
            </p>
          )}
          {/* O link "Saiba mais" do aviso leva a outra tela: nesse caso, "Depois". */}
          <div
            onClick={(e) => {
              if (e.target instanceof Element && e.target.closest('a') !== null) onDismiss();
            }}
          >
            <MonitoringBanner />
          </div>
          <div className="flex gap-3 pt-2">
            {canPair ? (
              <>
                <button type="button" onClick={onDismiss} className={BTN_SECONDARY}>
                  Depois
                </button>
                <button
                  type="button"
                  data-autofocus
                  onClick={() => setStep('qr')}
                  className={BTN_PRIMARY}
                >
                  Conectar agora
                </button>
              </>
            ) : (
              <button type="button" data-autofocus onClick={onDismiss} className={BTN_PRIMARY}>
                Entendi
              </button>
            )}
          </div>
        </Shell>
      ) : null}
      {step === 'qr' ? (
        <Shell titleId="connect-wa-title">
          <Title id="connect-wa-title">Escaneie o código com o celular</Title>
          <PolicyAcceptanceGate policyKey={WHATSAPP_MONITORING_POLICY_KEY}>
            <PairingQrPanel
              status={status ?? 'STOPPED'}
              hasChannel={me.data?.channel_id !== null && me.data?.channel_id !== undefined}
              autoStart
              bare
            />
          </PolicyAcceptanceGate>
          <button type="button" onClick={onDismiss} className={BTN_SECONDARY}>
            Depois
          </button>
        </Shell>
      ) : null}
      {step === 'success' ? (
        <Shell titleId="connect-wa-title">
          <Title id="connect-wa-title">WhatsApp conectado</Title>
          <p role="status" className="font-sans text-sm text-ink-2">
            Pronto! Seu celular está conectado ao sistema.
          </p>
          <button type="button" data-autofocus onClick={onFinish} className={BTN_PRIMARY}>
            Fechar
          </button>
        </Shell>
      ) : null}
    </>,
    document.body,
  );
}

function ConnectPromptController(): React.JSX.Element | null {
  const { open, dismiss, finish } = useConnectPrompt();
  return open ? <ConnectDialog onDismiss={dismiss} onFinish={finish} /> : null;
}

/**
 * Convite montado no AppLayout. Para quem não tem a flag ou a permissão não
 * monta nada — nem faz a consulta do estado do aparelho.
 */
export function ConnectWhatsAppPrompt(): React.JSX.Element | null {
  const eligible = useConnectPromptEligibility();
  return eligible ? <ConnectPromptController /> : null;
}
